// client/src/routes/app/contracts/new.tsx — SCR-CON-002 · CON-005 (04 §8.4, §5.14).
//
// `Leasing.Write`, not `Leasing.Read`: an accountant can see the contracts and cannot draw one
// up (03 §17.1), and the client mirror says so before the API's 403 does.
//
// A created contract is a DRAFT — CON-001 has no „create active" path, and it should not: the
// terms are still editable in place until somebody activates it, and activation is the act that
// generates the rent schedule. So the wizard lands on the new contract's DETAIL, where the
// „Activează" button is, rather than back on the list. The toast says a draft was created, not
// „contract creat", because those are different things to a landlord.
//
// `?propertyId=` is where „Contract nou" on a property's profile sends the wizard (D-2840): the
// apartment is already chosen, and „Renunțați" and the way back return to that profile's
// contracts tab rather than to the list the landlord never opened.
//
// `?tenantId=` is its twin from the tenant's side (D-2880): „Contract nou" on a tenant's row, on
// the tenant's profile, and „Adăugați chiriaș și contract" on the new-tenant form all land here
// with that tenant already on the contract as its titular. The way back is then the tenant's
// „Locuințe" tab, which is where the contract will show once it exists. When both are given the
// property wins the way back: it is the more specific place the landlord came from.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { ContractWizard } from '@/features/contracts/ContractWizard';
import { useCreateContract } from '@/features/contracts/queries';
import {
  prefilledContractForm,
  toContractInput,
  type ContractFormValues,
} from '@/features/contracts/schema';
import { useTenant } from '@/features/tenants/queries';
import { toast } from '@/stores/toast-store';

const newContractSearchSchema = z.object({
  propertyId: z.string().uuid().optional(),
  tenantId: z.string().uuid().optional(),
});


export const Route = createFileRoute('/app/contracts/new')({
  validateSearch: newContractSearchSchema,
  component: NewContractScreen,
});

function NewContractScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateContract();
  const { propertyId, tenantId } = Route.useSearch();
  // Only for the way back's label; the wizard's own picker names the party.
  const tenant = useTenant(tenantId ?? '', { enabled: tenantId !== undefined });
  const fromTenant = propertyId === undefined && tenantId !== undefined;

  const leave = () => {
    if (propertyId !== undefined) {
      void navigate({ to: '/app/properties/$propertyId/contracts', params: { propertyId } });
    } else if (tenantId !== undefined) {
      void navigate({ to: '/app/tenants/$tenantId/homes', params: { tenantId } });
    } else {
      void navigate({ to: '/app/contracts' });
    }
  };

  return (
    <RequirePermission permission="Leasing.Write">
      <PageHeader
        title={t('contracts.wizard.createTitle')}
        description={t('contracts.wizard.createLead')}
        backTo={
          propertyId !== undefined
            ? '/app/properties/$propertyId/contracts'
            : fromTenant
              ? '/app/tenants/$tenantId/homes'
              : '/app/contracts'
        }
        backParams={
          propertyId !== undefined ? { propertyId } : fromTenant ? { tenantId } : undefined
        }
        backLabel={
          propertyId !== undefined
            ? t('properties.contracts.title')
            : fromTenant
              ? (tenant.data?.displayName ?? t('tenants.list.title'))
              : t('contracts.list.title')
        }
      />
      <ContractWizard
        initialValues={prefilledContractForm(propertyId, tenantId)}
        submitLabel={t('contracts.wizard.create')}
        onSubmit={async (values: ContractFormValues) => {
          const created = await create.mutateAsync({ body: toContractInput(values) });
          toast.success(t('contracts.wizard.created', { number: created.contractNumber }));
          await navigate({ to: '/app/contracts/$contractId', params: { contractId: created.id } });
        }}
        onCancel={leave}
      />
    </RequirePermission>
  );
}
